import { Alert, Box, Tooltip } from "@mui/material"
import { UseQueryResult } from "@tanstack/react-query"
import log from "loglevel"
import { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { NexSelect } from "../../components/Fields/NexSelect"
import { LoadingButton } from "../../components/LoadingButton/LoadingButton"
import { AlertModal } from "../../components/Modals/AlertModal"
import { Step, StepperForm } from "../../components/StepperForm/StepperForm"
import { MissionTitle } from "../../components/Titles/MissionTitle"
import { ProjectTitle } from "../../components/Titles/ProjectTitle"
import { Body } from "../../components/Typography/Typography"
import { useMissions } from "../../hooks/useMissions"
import { useNexusNavigate } from "../../hooks/useNexusNavigate"
import {
    useNexusFlightplanIds,
    useNexusMissionFlow,
    useNexusMissionIdOptional,
    useNexusProjectNumber,
} from "../../hooks/useNexusParams"
import { useTitle } from "../../hooks/useTitle"
import { useToggle } from "../../hooks/useToggle"
import { SensorList } from "../../models/sensors/Sensor"
import { ConfirmationRoutes } from "../../routing/NexRoutes"
import { RootContext } from "../../store/Context"
import {
    deleteMission,
    fetchAirportList,
    fetchEmployeeList,
    fetchMission,
    fetchPlatformList,
    fetchVendorNameList,
    updateMission,
} from "../../store/actions/MissionsActions"
import { ToolbarOptions } from "../../store/types/AuthTypes"
import { FlightplanStatus } from "../../store/types/FlightPlansTypes"
import { MissionActiveFlow, MissionType } from "../../store/types/MissionsTypes"
import { Types } from "../../store/types/Types"
import { TimeUtilsDateFns } from "../../utils/TimeUtils"
import { useAppErrorStore } from "../../zstore/AppErrorStore"
import { useAuthStore } from "../../zstore/AuthStore"
import { useFlightlineEventStore } from "../../zstore/FlightlineEventStore"
import { useShippingInfoStore } from "../../zstore/ShippingInfoStore"
import { FlightData } from "./FlightData"
import { FlightlineDetails } from "./FlightlineDetails"
import { MissionNotes } from "./MissionNotes"
import { TeamIdentification } from "./TeamIdentification"
import { MissionStep, getMissionValidatorModel } from "./model"
import { useStyles } from "./styles"

const initialSteps: Step[] = [
    { label: "Team Identification", key: MissionStep.TEAM_IDENTIFICATION },
    { label: "Mission Notes", key: MissionStep.NOTES },
    { label: "Flight Data", key: MissionStep.FLIGHT_DATA },
    { label: "Flightline Details", key: MissionStep.FLIGHTLINE_DETAILS },
]

const getStepContent = (activeStep: number) => {
    switch (activeStep) {
        case 0:
            return <TeamIdentification />
        case 1:
            return <MissionNotes />
        case 2:
            return <FlightData />
        case 3:
            return <FlightlineDetails />
        default:
            return "Error loading content"
    }
}

interface MissionEditorProps {
    sensorListResponse: UseQueryResult<SensorList, Error>
}

export const MissionEditor = (props: MissionEditorProps) => {
    useTitle("Mission Editor")
    const navigate = useNavigate()
    const { classes, cx } = useStyles()
    const projectNumber = useNexusProjectNumber()
    const missionFlow = useNexusMissionFlow()
    const missionId = useNexusMissionIdOptional()
    const flightPlanIds = useNexusFlightplanIds()
    const { dispatch } = useContext(RootContext)

    const checkToolbarAccess = useAuthStore((state) => state.checkToolbarAccess)
    const clearAppError = useAppErrorStore((state) => state.clear)

    const fetchFlightlineEvents = useFlightlineEventStore((state) => state.fetchFlightlineEvents)
    const fetchAllFlightPlans = useFlightlineEventStore((state) => state.fetchAllFlightPlans)
    const fetchEventPlannedLines = useFlightlineEventStore((state) => state.fetchEventPlannedLines)
    const flightlineEvents = useFlightlineEventStore((state) => state.flightlineEvents)
    const selectedFlightplans = useFlightlineEventStore((state) => state.selectedFlightplans)
    const curRegexExpression = useFlightlineEventStore((state) => state.curRegexExpression)
    const flightlineEventErrorMessage = useFlightlineEventStore((state) => state.error)
    const updateFlightlineEvents = useFlightlineEventStore((state) => state.updateFlightlineEvents)
    const resetFlightlineEventState = useFlightlineEventStore((state) => state.resetState)

    const fetchShippingInfo = useShippingInfoStore((state) => state.fetchShippingInfo)
    const allShippingInfo = useShippingInfoStore((state) => state.allShippingInfoWrappers)
    const shippingInfoErrorMessage = useShippingInfoStore((state) => state.shippingInfoErrorMessage)
    const clearShippingInfoState = useShippingInfoStore((state) => state.clearState)

    const { missions, isNoFlight, isNoAcquisition, isNoFile, isFullFlight, handleMissionDataChange } = useMissions()
    const {
        selectedMission,
        selectedMissionId,
        isVendorFlow,
        isDeleting,
        parsedMissions,
        activeFlow,
        warnings,
        missionsErrorMessage,
    } = missions

    const { sensorListResponse } = props
    const sensorListResult = sensorListResponse?.data
    const sensorListErrorMessage = sensorListResponse.error?.message

    const isEditFlow = missionFlow === "edit"
    const isDeleteDisabled = !checkToolbarAccess([ToolbarOptions.Edit_Blog_Entries, ToolbarOptions.Mission_Admin])
    const isFlightplanCompleted =
        !!selectedFlightplans.length && selectedFlightplans[0].status === FlightplanStatus.Complete

    const [selectedParsedMissionIndex, setSelectedParsedMissionIndex] = useState(0)
    const [steps, setSteps] = useState(initialSteps)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [activeStep, setActiveStep] = useState(0)
    const [deleteModalOpen, toggleDeleteModalOpen] = useToggle()
    const nexusNavigate = useNexusNavigate()

    const selectedParsedMission = parsedMissions[selectedParsedMissionIndex]

    const setNoFlightSteps = () => {
        setSteps(steps.slice(0, 2))
    }
    const setNoAcquisitionSteps = () => {
        setSteps(steps.slice(0, 3))
    }

    // fetch data
    useEffect(() => {
        clearAppError()
        if (isEditFlow && missionId) {
            resetFlightlineEventState()
        }

        const reqs = []
        // get lists
        if (projectNumber) {
            reqs.push(fetchPlatformList(dispatch, projectNumber!))
            reqs.push(fetchEmployeeList(dispatch, projectNumber!))
            reqs.push(fetchVendorNameList(dispatch))
            reqs.push(fetchAirportList(dispatch, projectNumber!))

            // In mission submit we will have flight plan ids included, and we want to select those for submitting this mission
            fetchAllFlightPlans(projectNumber, flightPlanIds.length > 0 ? flightPlanIds : undefined)
        }
        // edit
        if (isEditFlow && missionId) {
            reqs.push(fetchMission(dispatch, missionId!))

            // This sets the selected flight plans
            reqs.push(fetchFlightlineEvents(missionId))
            fetchShippingInfo(missionId)
        } else {
            if (flightPlanIds.length > 0) {
                // We fetch the event planned lines via the flightplan ids from the URL
                fetchEventPlannedLines(flightPlanIds)
            }
        }

        // flight types
        if (isNoFlight) {
            setNoFlightSteps()
        }
        if (isNoAcquisition) {
            setNoAcquisitionSteps()
        }
        if (!isFullFlight) {
            handleMissionDataChange({ flown_date: TimeUtilsDateFns.formatDate(new Date()) })
        }

        Promise.all(reqs).then(() => {
            setLoading(false)
        })
        return () => {
            clearShippingInfoState()
        }
    }, [])

    // init after loading done
    useEffect(() => {
        if (!loading) {
            // parsed missions index found by display name
            // saved before making further changes to selectedMission
            if (parsedMissions.length) {
                handleMissionSelect(selectedParsedMission.mission_name)
            }

            // edit init
            if (isEditFlow) {
                let flow = MissionActiveFlow.FULL_FLIGHT

                if (selectedMission.flight_num === null) {
                    setNoFlightSteps()
                    flow = MissionActiveFlow.NO_FLIGHT
                } else if (selectedMission.source_file === null) {
                    log.info(`Flightline events in edit init ... ${flightlineEvents.length}`)
                    if (flightlineEvents.length === 0) {
                        setNoAcquisitionSteps()
                        flow = MissionActiveFlow.NO_ACQUISITION
                    } else {
                        flow = MissionActiveFlow.NO_FILE
                    }
                }

                dispatch({
                    type: Types.Missions_SelectActiveFlow,
                    payload: { activeFlow: flow },
                })
            }
        }
    }, [loading])

    /**
     * Navigate to confirmation page after saving mission name.
     */
    const handleConfirmation = () => {
        nexusNavigate.confirmationPage(
            isEditFlow ? ConfirmationRoutes.MISSION_EDITED : ConfirmationRoutes.MISSION_SUBMITTED,
            {
                entityName: selectedMission.mission_name,
                projectNumber,
            },
        )
    }

    /**
     * Move back or forward one step. If out of forward steps,
     * attempt to submit mission.
     */
    const handleStepChange = async (newStep: number) => {
        if (newStep === steps.length) {
            setSubmitting(true)
            const newMission: MissionType = { ...selectedMission, project_number: projectNumber }

            const success = await updateMission(
                dispatch,
                newMission,
                flightlineEvents,
                allShippingInfo,
                isNoFile,
                isVendorFlow,
                isNoFlight,
            )
            setSubmitting(false)
            if (success) {
                handleConfirmation()
            }
        } else if (newStep === -1) {
            navigate(-1)
        } else {
            setActiveStep(newStep)
        }
    }

    const handleErrorClose = () => {
        dispatch({ type: Types.Missions_ClearError })
    }

    /**
     * Select mission.
     */
    const handleMissionSelect = (selection: string) => {
        const index = parsedMissions.findIndex((m) => m.mission_name === selection)
        setSelectedParsedMissionIndex(index)

        const selectedParsedMission = parsedMissions[index]
        updateFlightlineEvents(selectedParsedMission.flightline_events)

        dispatch({
            type: Types.Missions_SelectParsedMission,
            payload: { selectedMission: parsedMissions[index], sensors: sensorListResult?.sensors ?? [] },
        })
    }

    const handleDelete = async () => {
        toggleDeleteModalOpen()

        if (selectedMissionId) {
            const success = await deleteMission(dispatch, selectedMissionId)
            if (success) {
                nexusNavigate.goHome()
            }
        }
    }

    return (
        <StepperForm
            steps={steps}
            activeStep={activeStep}
            loading={loading || sensorListResponse.isLoading}
            submitting={submitting}
            warnings={warnings}
            onStepChange={handleStepChange}
            onErrorClose={handleErrorClose}
            validationProps={{
                dependencies: [
                    selectedMission,
                    flightlineEvents,
                    allShippingInfo,
                    curRegexExpression,
                    activeFlow,
                    isVendorFlow,
                ],
                model: getMissionValidatorModel(
                    selectedMission,
                    flightlineEvents,
                    allShippingInfo,
                    activeFlow,
                    isVendorFlow,
                ),
            }}
            errorMessages={[missionsErrorMessage]}
            header={
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <ProjectTitle projectNumber={projectNumber} />

                    {!loading && !isEditFlow && parsedMissions.length > 1 && (
                        <Box>
                            <Body>
                                The uploaded files contained multiple missions. Select which channel or mission you'd
                                like to import.
                            </Body>

                            <NexSelect
                                id="select-channel"
                                value={selectedParsedMission ? selectedParsedMission.mission_name : ""}
                                options={parsedMissions.map((m) => m.mission_name)}
                                onChange={(e) => handleMissionSelect(e.target.value)}
                                width="md"
                                disableDefaultValue
                            />
                        </Box>
                    )}

                    <MissionTitle loading={loading} missionName={selectedMission.mission_name} />

                    <AlertModal
                        danger
                        open={deleteModalOpen}
                        pending={isDeleting}
                        title="Delete Mission?"
                        onConfirm={handleDelete}
                        onClose={toggleDeleteModalOpen}
                    >
                        Are you sure you want to delete this mission and its associated flightline events? This action
                        cannot be reversed.
                        {isFlightplanCompleted && (
                            <>
                                <br />
                                <br />
                                <span>
                                    Flightplan <strong>{selectedFlightplans[0]?.flight_plan_name}</strong> utilized by
                                    this mission currently has the Complete status. Status will be reset to Active for
                                    this flightplan.
                                </span>
                            </>
                        )}
                    </AlertModal>
                </Box>
            }
            disableSubmit={isDeleting || !selectedMission.flight_notes}
            secondaryAction={
                isEditFlow && (
                    <Tooltip arrow title={isDeleteDisabled ? "Missions can be deleted by Acquisition Managers" : ""}>
                        <div>
                            <LoadingButton
                                disabled={isDeleteDisabled || submitting}
                                onClick={toggleDeleteModalOpen}
                                pending={isDeleting}
                                variant="outlined"
                                color="primary"
                                size="large"
                                className={cx(classes.btn_delete)}
                            >
                                Delete
                            </LoadingButton>
                        </div>
                    </Tooltip>
                )
            }
        >
            {/* There is no way to delete these alerts manually as they are handled from API calls */}
            {sensorListErrorMessage && <Alert severity="error">{sensorListErrorMessage}</Alert>}
            {/* TODO - These errors ARE handled with zustand but we might want to move the API calls
                    to tanstack - currently no way to clear the error either */}
            {flightlineEventErrorMessage && <Alert severity="error">{flightlineEventErrorMessage}</Alert>}
            {shippingInfoErrorMessage && <Alert severity="error">{shippingInfoErrorMessage}</Alert>}
            {getStepContent(activeStep)}
        </StepperForm>
    )
}
